/**
 * Tooltip — the vendored tooltip composition (WEB TIER A · WT2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `@radix-ui/react-tooltip` HAS BEEN INSTALLED AND USED ZERO TIMES — the
 * shadcn sandbox verdict named that as one of the four things this app
 * actually lacks. Same vendoring charter as `Modal.tsx`: CRF-owned source,
 * individual Radix package, no shadcn CLI or runtime. First consumers arrive
 * with the Tier A page rebuilds (the worklist age cell's "raised {date}" and
 * the masked-marker explanation surface — WT3); WT2 ships the composition
 * only. Adoption beyond those two is FILED, not folded
 * (docs/WEB-TIER-A-LEDGER.md item 5).
 *
 * THE TIP IS A SURFACE CARD, NOT AN INK BUBBLE, deliberately: `bg-ink` is
 * mode-invariant #1c1c1c, which on the dark theme's #1f1f1f surfaces would be
 * an edgeless near-black blob. `bg-surface` + `border-line` +
 * `--crf-shadow-overlay` is the app's one overlay grammar (menus, dialogs) and
 * re-maps itself per theme — which is also why this file needs no `dark:`
 * utility at all. Text is `text-fg` at `text-label`: 12px quiet chrome, full
 * contrast (13.25 light / 12.06 dark vs surface).
 *
 * SANDBOX-MEASURED FIXES folded and pinned by `vendored-source.test.ts`: no
 * `.dark` selector, no `disabled:opacity`, no alpha-fade states, authored
 * radii only (`rounded-sm`), no `text-fg-muted`.
 *
 * A NOTE ON WHAT A TOOLTIP MAY CARRY: supplementary phrasing of something the
 * screen already states or marks — never the ONLY carrier of a fact. Radix
 * wires `aria-describedby` from trigger to content, opens on focus as well as
 * hover, and closes on Escape; the trigger must therefore be a focusable
 * element (the `asChild` child).
 */

import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

export interface TooltipProps {
  /** The supplementary sentence. Text, or restrained inline markup. */
  content: ReactNode;
  /** The trigger — rendered `asChild`, so it must accept a ref and be focusable. */
  children: ReactNode;
  side?: ComponentPropsWithoutRef<typeof RadixTooltip.Content>['side'];
  /**
   * Radix's default is 700ms; 300 keeps the app's "quick but not twitchy"
   * feel. Tests pass 0. Motion stays within the 150ms settle ceiling because
   * the delay is a wait, not a transition.
   */
  delayDuration?: number;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  delayDuration = 300,
}: TooltipProps): React.JSX.Element {
  return (
    // Provider-per-instance keeps the component self-contained (no app-shell
    // wrapper to forget); Radix de-duplicates timing state per Provider, and
    // the skip-delay window matters within one trigger cluster, not across the
    // whole page.
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={6}
            className="z-50 max-w-[280px] rounded-sm border border-line bg-surface px-2.5 py-1.5 text-label text-fg shadow-[var(--crf-shadow-overlay)]"
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
